"use client";

import { useCallback, useMemo } from "react";
import { usePathname } from "next/navigation";

import {
  LimitedTimeOfferIcon,
  LimitedTimeUpgradeArrowIcon,
  XIcon,
} from "@/components/fun-ui/icons";
import { Tip } from "@/components/fun-ui/Tip";
import { cn } from "@/lib/utils";
import { usePayment, useUser } from "@/stores/appStore";
import { useVipRenewalIncentiveRuntime } from "@/stores/vipRenewalIncentiveRuntimeStore";
import type { TouchpointItem } from "@/stores/slices/guideSlice";
import {
  getActiveVipIncentiveOffer,
  getVipIncentiveCopy,
  inactiveVipIncentiveOffer,
  VipIncentiveOffer,
  useVipRenewalIncentiveCountdown,
} from "@/components/payment/vipRenewalIncentive";

type Countdown = {
  hours: string;
  minutes: string;
  seconds: string;
};

type VipRenewalIncentiveFooterAdState = {
  visible: boolean;
  countdown: Countdown;
  footerText: string;
  footerCompactText: string;
  ctaLabel: string;
  close: () => void;
  upgrade: () => void;
};

function CountdownCell({ value }: { value: string }) {
  return (
    <span
      className="flex h-6 w-[22px] items-center justify-center rounded-sm bg-background text-sm font-medium leading-[19px]"
      style={{ color: "var(--vip-renewal-incentive-badge-border)" }}
    >
      {value}
    </span>
  );
}

function CountdownDivider() {
  return (
    <span
      className="flex h-6 w-2 items-center justify-center text-sm font-medium leading-none"
      style={{ color: "var(--vip-renewal-incentive-badge-border)" }}
    >
      :
    </span>
  );
}

function isFooterAdPath(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  return normalizedPathname === "/papers"
    || /^\/subjects\/[^/]+$/.test(normalizedPathname);
}

function isModalEntryPath(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  return normalizedPathname === "/papers";
}

function getTouchpointVipOffer(item?: TouchpointItem | null): VipIncentiveOffer {
  if (!item) {
    return inactiveVipIncentiveOffer;
  }
  const remainingSeconds = Number(item.payload?.remainingSeconds);
  const endTime = Number(item.payload?.endTime);
  if (!Number.isFinite(remainingSeconds) || !Number.isFinite(endTime)) {
    return inactiveVipIncentiveOffer;
  }
  if (item.type === "first_offer") {
    const amount = Number(item.payload?.bonusBeans);
    if (!Number.isFinite(amount) || amount <= 0) {
      return inactiveVipIncentiveOffer;
    }
    return {
      active: true,
      kind: "first",
      remainingSeconds,
      amount,
      endTime,
    };
  }
  if (item.type === "recovery_offer") {
    const amount = Number(item.payload?.recoverableAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return inactiveVipIncentiveOffer;
    }
    return {
      active: true,
      kind: "recovery",
      remainingSeconds,
      amount,
      endTime,
    };
  }
  return inactiveVipIncentiveOffer;
}

export function useVipRenewalIncentiveFooterAdState(
  item?: TouchpointItem | null,
  onDismiss?: (item: TouchpointItem) => Promise<void> | void,
  disableLegacy = false,
): VipRenewalIncentiveFooterAdState {
  const pathname = usePathname();
  const {
    firstOffer,
    recoveryOffer,
    expireVipIncentiveOffer,
    refreshUserInfo,
  } = useUser();
  const { openPayment } = usePayment();
  const {
    dismissedEndTimes,
    setDismissedEndTime,
  } = useVipRenewalIncentiveRuntime();
  const incentiveOffer = useMemo(
    () => item ? getTouchpointVipOffer(item) : disableLegacy ? inactiveVipIncentiveOffer : getActiveVipIncentiveOffer({
      firstOffer,
      recoveryOffer,
    }),
    [disableLegacy, firstOffer, item, recoveryOffer],
  );
  const handleOfferExpire = useCallback((offer: { kind: "first" | "recovery" }) => {
    if (item) {
      return;
    }
    if (offer.kind === "first") {
      void refreshUserInfo();
      return;
    }
    expireVipIncentiveOffer(offer.kind);
  }, [expireVipIncentiveOffer, item, refreshUserInfo]);
  const {
    remainingSeconds,
    countdown,
    offerBeans,
  } = useVipRenewalIncentiveCountdown(incentiveOffer, handleOfferExpire);
  const copy = useMemo(
    () => getVipIncentiveCopy({
      offer: incentiveOffer,
      offerBeans,
    }),
    [incentiveOffer, offerBeans],
  );

  const offerEndTime = incentiveOffer.active ? incentiveOffer.endTime : 0;
  const modalDismissedEndTime = incentiveOffer.active
    ? dismissedEndTimes.modal[incentiveOffer.kind]
    : 0;
  const footerAdDismissedEndTime = incentiveOffer.active
    ? dismissedEndTimes.footerAd[incentiveOffer.kind]
    : 0;
  const modalWouldOpen = incentiveOffer.active
    && isModalEntryPath(pathname)
    && offerEndTime > 0
    && remainingSeconds > 0
    && modalDismissedEndTime !== offerEndTime;
  const visible = useMemo(() => (
    item
      ? incentiveOffer.active && remainingSeconds > 0
      : isFooterAdPath(pathname)
        && incentiveOffer.active
        && offerEndTime > 0
        && remainingSeconds > 0
        && !modalWouldOpen
        && footerAdDismissedEndTime !== offerEndTime
  ), [
    footerAdDismissedEndTime,
    modalWouldOpen,
    offerEndTime,
    pathname,
    incentiveOffer.active,
    item,
    remainingSeconds,
  ]);

  const close = useCallback(() => {
    if (item) {
      void onDismiss?.(item);
      return;
    }
    if (incentiveOffer.active && offerEndTime > 0) {
      setDismissedEndTime("footerAd", incentiveOffer.kind, offerEndTime);
    }
  }, [incentiveOffer, item, offerEndTime, onDismiss, setDismissedEndTime]);

  const upgrade = useCallback(() => {
    openPayment({
      open: true,
      type: "upgrade",
    });
  }, [openPayment]);

  return {
    visible,
    countdown,
    footerText: copy.footerText,
    footerCompactText: copy.badge,
    ctaLabel: copy.footerCtaLabel,
    close,
    upgrade,
  };
}

export function VipRenewalIncentiveFooterAd({
  state,
  className = "",
}: {
  state: VipRenewalIncentiveFooterAdState;
  className?: string;
}) {
  if (!state.visible) {
    return null;
  }

  return (
    <div
      className={cn(
        "pointer-events-auto absolute left-1/2 top-1/2 z-10 h-9 w-fit",
        "-translate-x-1/2 -translate-y-1/2",
        className,
      )}
    >
      <div
        className="vip-renewal-footer-ad-shell relative h-full w-fit"
      >
        <button
          type="button"
          className={cn(
            "vip-renewal-footer-ad-button flex h-full w-fit items-center justify-center rounded-lg py-0 pl-5 pr-4",
            "bg-[hsl(var(--vip-renewal-incentive-footer-bg))] text-foreground",
            "backdrop-blur-[10px] transition-colors hover:bg-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          )}
          onClick={state.upgrade}
        >
          <span className="hidden items-center xl:flex">
            <span className="flex items-center gap-2">
              <LimitedTimeOfferIcon
                aria-hidden="true"
                size={16}
                color="hsl(var(--foreground))"
                className="shrink-0"
              />
              <span className="whitespace-nowrap text-sm font-normal leading-[19px]">
                {state.footerText}
              </span>
            </span>

            <span className="ml-3 grid h-6 w-[82px] grid-cols-[22px_8px_22px_8px_22px] items-center">
              <CountdownCell value={state.countdown.hours} />
              <CountdownDivider />
              <CountdownCell value={state.countdown.minutes} />
              <CountdownDivider />
              <CountdownCell value={state.countdown.seconds} />
            </span>

            <span
              className="ml-4 flex items-center gap-1 whitespace-nowrap text-sm font-medium leading-[19px]"
              style={{ color: "var(--vip-renewal-incentive-badge-border)" }}
            >
              {state.ctaLabel}
              <LimitedTimeUpgradeArrowIcon
                aria-hidden="true"
                size={16}
                color="var(--vip-renewal-incentive-badge-border)"
                className="shrink-0"
              />
            </span>
          </span>

          <span className="flex items-center xl:hidden">
            <span className="flex items-center gap-2">
              <LimitedTimeOfferIcon
                aria-hidden="true"
                size={16}
                color="hsl(var(--foreground))"
                className="shrink-0"
              />
              <span className="whitespace-nowrap text-sm font-normal leading-[19px]">
                {state.footerCompactText}
              </span>
            </span>

            <span
              className="ml-4 flex items-center gap-1 whitespace-nowrap text-sm font-medium leading-[19px]"
              style={{ color: "var(--vip-renewal-incentive-badge-border)" }}
            >
              {state.ctaLabel}
              <LimitedTimeUpgradeArrowIcon
                aria-hidden="true"
                size={16}
                color="var(--vip-renewal-incentive-badge-border)"
                className="shrink-0"
              />
            </span>
          </span>
        </button>

        <div
          className={cn(
            "vip-renewal-footer-ad-close-area pointer-events-none absolute left-full top-0 z-20 -ml-2 flex h-full w-[46px] items-center justify-start rounded-r-lg pl-2",
            "bg-[hsl(var(--vip-renewal-incentive-footer-bg))] opacity-0 backdrop-blur-[10px] transition-colors",
          )}
        >
          <Tip label="关闭" side="top" sideOffset={8} truncate={false} asChild>
            <button
              type="button"
              aria-label="关闭限时续费提示"
              className={cn(
                "flex size-[18px] items-center justify-center rounded-full bg-background p-0 shadow-sm",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              )}
              onClick={(event) => {
                event.stopPropagation();
                state.close();
              }}
            >
              <XIcon
                aria-hidden="true"
                size={14}
                color="hsl(var(--annotation-foreground))"
              />
            </button>
          </Tip>
        </div>
        <style jsx>{`
          .vip-renewal-footer-ad-shell:hover .vip-renewal-footer-ad-button,
          .vip-renewal-footer-ad-shell:focus-within .vip-renewal-footer-ad-button {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }

          .vip-renewal-footer-ad-shell:hover .vip-renewal-footer-ad-close-area,
          .vip-renewal-footer-ad-shell:focus-within .vip-renewal-footer-ad-close-area {
            opacity: 1;
            pointer-events: auto;
          }
        `}</style>
      </div>
    </div>
  );
}
